import React from 'react';

/*
 * Short, player-facing summary of the Epic Hero Game design
 * document (Nov 4, 2026).
 */

const pillars = [
  ['Hits you can feel', 'Every fighter is an active ragdoll, so punches shove bodies and giants stagger.'],
  ['The world scales with you', 'From islands to galaxies.'],
  ['Build anything from anything', 'About 200 items connect through recipe trees, from a simple spell to a fire beam.'],
  ['The world is a toy', 'Throw, smash and climb anything.'],
];

const stages = [
  ['Islands', 'Sail between small islands on a raft.'],
  ['Moons', 'Your first rocket flights, around your home planet.'],
  ['Galaxy', 'Pick one of four solar systems.'],
  ['Solar system', 'Pick one of four planets.'],
  ['Planet', 'Clear its moons, then beat the final boss.'],
];

const powerScale = [
  ['Islands', 'A sword', 'Common, uncommon'],
  ['Moons', 'TNT-sized blasts', 'Rare'],
  ['Planets', 'Nuclear', 'Ultimate'],
  ['Galaxy', 'Planet-breaking', 'Mega'],
];

const craftingTiers = [
  ['Base', 'Spellcast', 'Your starter spell'],
  ['Element', '2 spellcasts + an element crystal', 'On-hit fire, freeze, lightning, toxic ball'],
  ['Form', 'A form item + an element spell', 'Fire beam, ice tornado, poison wall'],
  ['Big', 'Several of the above', 'Nuke, tsunami, rock golem, meteor shower'],
];

const Table = ({ head, rows }) => (
  <div className="design-table">
    <table>
      <thead>
        <tr>
          {head.map((h) => <th key={h}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[0]}>
            {row.map((cell, i) => <td key={i}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DesignDoc = () => (
  <section id="design" className="section design">
    <div className="container container--narrow">
      <div className="section-head">
        <h2>Game design</h2>
        <span className="label">Updated Nov 4, 2026</span>
      </div>

      <p className="design-lead">
        Epic Hero Game is a physics-driven action roguelite. You start with bare
        fists on a small island and end strong enough to break planets.
      </p>

      <div className="design-pillars">
        {pillars.map(([title, text]) => (
          <div key={title} className="design-pillar">
            <h4>{title}</h4>
            <p>{text}</p>
          </div>
        ))}
      </div>

      <section className="design-part">
        <h3>The run</h3>
        <ol className="design-stages">
          {stages.map(([title, text]) => (
            <li key={title}>
              <span className="design-stages__name">{title}</span>
              <span className="design-stages__text">{text}</span>
            </li>
          ))}
        </ol>
        <p>
          Each planet&rsquo;s moons form a map of locked routes. Boss keys open
          shields, better rockets get past asteroid rings, and clearing a world
          lifts the fog around it. Finish the galaxy and a harder one rolls.
        </p>
      </section>

      <section className="design-part">
        <h3>Loot and builds</h3>
        <p>Each stage is a jump in your power, and the loot is designed to follow it.</p>
        <Table head={['Stage', 'Your power', 'Loot tier']} rows={powerScale} />
        <p>
          Most items are built from other items, in your pouch or at a lizard
          shop, and the shop shows the whole tree.
        </p>
        <Table head={['Tier', 'Recipe', 'Examples']} rows={craftingTiers} />
        <p>
          Nine elements run through the spell trees: fire, ice, lightning, wind,
          earth, water, poison, arcane and shadow.
        </p>
      </section>
    </div>
  </section>
);

export default DesignDoc;
